import React, { useState, useRef, useEffect } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';

interface ProjectFilterSliderProps {
  onFilterChange?: (filter: string) => void;
  defaultFilter?: string;
  width?: string; // Width prop for customization
}

export default function ProjectFilterSlider({
  onFilterChange,
  defaultFilter = 'All',
  width = '100%', // Use full width of container by default
}: ProjectFilterSliderProps) {
  const [activeFilter, setActiveFilter] = useState(defaultFilter);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const [scrollPosition, setScrollPosition] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  // Ref to track if the scroll is user-initiated or programmatic
  const isAutoScrollingRef = useRef(false);

  // Project status filters based on your application
  const filters = [
    'All',
    'Unassigned',
    'Assigned',
    'In Progress',
    'Review',
    'Delivered',
    'Billed',
    'Unpaid Reopen',
    'Paid Reopen',
    'Payment Received',
    'Cancelled',
    'On Hold',
    'Non Active',
  ];

  // Update indicator position whenever needed
  const updateIndicator = () => {
    if (buttonRefs.current[activeFilter] && containerRef.current && scrollContainerRef.current) {
      const button = buttonRefs.current[activeFilter];
      const containerRect = containerRef.current.getBoundingClientRect();
      const buttonRect = button!.getBoundingClientRect();
      const scrollLeft = scrollContainerRef.current.scrollLeft;

      // Calculate the correct position considering scroll offset
      // Now setting height to 100% and removing any margins/paddings that could create gaps
      setIndicatorStyle({
        width: buttonRect.width,
        left: buttonRect.left - containerRect.left + scrollLeft,
        height: '100%',
        position: 'absolute',
        top: 0,
        borderRadius: '9999px', // Full rounded corners
        backgroundColor: 'rgb(6 182 212)', // tailwind's cyan-500
        transition: 'all 300ms ease-in-out',
      });
    }
  };

  // Check if scrolling is possible in each direction
  const updateScrollButtons = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;

      // If scrolled at all, show the left button
      setCanScrollLeft(scrollLeft > 0);

      // If not scrolled all the way to the right, show right button
      setCanScrollRight(scrollWidth > clientWidth + scrollLeft);
    }
  };

  // Scroll to make active filter visible
  const scrollToActiveFilter = () => {
    if (buttonRefs.current[activeFilter] && containerRef.current && scrollContainerRef.current) {
      const button = buttonRefs.current[activeFilter];
      const containerRect = containerRef.current.getBoundingClientRect();
      const buttonRect = button!.getBoundingClientRect();
      const scrollContainer = scrollContainerRef.current;

      const scrollLeft = scrollContainer.scrollLeft;
      const containerWidth = scrollContainer.clientWidth;

      // Position of button relative to container including scroll position
      const buttonLeft = buttonRect.left - containerRect.left + scrollLeft;
      const buttonRight = buttonLeft + buttonRect.width;

      // Check if button is not fully visible
      if (buttonLeft < scrollLeft) {
        // Button is to the left of the visible area
        isAutoScrollingRef.current = true;
        scrollContainer.scrollLeft = buttonLeft;
        setTimeout(() => {
          isAutoScrollingRef.current = false;
        }, 350); // Match duration with CSS transition
      } else if (buttonRight > scrollLeft + containerWidth) {
        // Button is to the right of the visible area
        isAutoScrollingRef.current = true;
        scrollContainer.scrollLeft = buttonRight - containerWidth;
        setTimeout(() => {
          isAutoScrollingRef.current = false;
        }, 350); // Match duration with CSS transition
      }
    }
  };

  // Initialize indicator and scroll on mount, and when active filter changes
  useEffect(() => {
    updateIndicator();
    scrollToActiveFilter();
    updateScrollButtons();

    window.addEventListener('resize', () => {
      updateIndicator();
      updateScrollButtons();
    });

    return () => {
      window.removeEventListener('resize', updateIndicator);
    };
  }, [activeFilter]);

  // Update active filter when defaultFilter prop changes
  useEffect(() => {
    if (defaultFilter && defaultFilter !== activeFilter) {
      setActiveFilter(defaultFilter);
    }
  }, [defaultFilter]);

  const handleScroll = () => {
    if (scrollContainerRef.current && !isAutoScrollingRef.current) {
      setScrollPosition(scrollContainerRef.current.scrollLeft);
      updateIndicator();
      updateScrollButtons();
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      isAutoScrollingRef.current = true;
      scrollContainerRef.current.scrollLeft -= 200;
      setTimeout(() => {
        isAutoScrollingRef.current = false;
        updateIndicator();
        updateScrollButtons();
      }, 350); // Match duration with CSS transition
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      isAutoScrollingRef.current = true;
      scrollContainerRef.current.scrollLeft += 200;
      setTimeout(() => {
        isAutoScrollingRef.current = false;
        updateIndicator();
        updateScrollButtons();
      }, 350); // Match duration with CSS transition
    }
  };

  const handleFilterClick = (filter: string) => {
    setActiveFilter(filter);
    if (onFilterChange) {
      onFilterChange(filter);
    }
  };

  return (
    <div className="flex items-start justify-start overflow-hidden">
      {/* Filter container - using custom width and left alignment */}
      <div className="relative" style={{ width }}>
        {/* Always show left arrow when there's content to scroll left */}
        <button
          onClick={scrollLeft}
          className={`absolute left-0 z-20 flex items-center justify-center w-8 h-8 bg-white border border-gray-200 rounded-full shadow-md transition-opacity duration-300 ${canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          style={{ top: '50%', transform: 'translateY(-50%)' }}
        >
          <MdChevronLeft size={16} className="text-gray-600" />
        </button>

        <div
          ref={containerRef}
          className="relative w-full border border-gray-200 rounded-full overflow-hidden h-11"
        >
          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto scrollbar-hide relative p-0 h-full"
            onScroll={handleScroll}
            style={{ scrollBehavior: 'smooth' }}
          >
            {/* Background indicator - now positioned with absolute styling */}
            <div className="z-0 absolute" style={indicatorStyle} />

            {/* Filter buttons - adjusted to have consistent height */}
            {filters.map(filter => (
              <button
                key={filter}
                ref={el => (buttonRefs.current[filter] = el)}
                onClick={() => handleFilterClick(filter)}
                className={`px-4 h-full flex items-center justify-center text-sm font-medium whitespace-nowrap rounded-full transition-colors relative z-10 flex-shrink-0 ${activeFilter === filter ? 'text-white' : 'text-gray-600'
                  }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>

        {/* Always show right arrow when there's content to scroll right */}
        <button
          onClick={scrollRight}
          className={`absolute right-0 z-20 flex items-center justify-center w-8 h-8 bg-white border border-gray-200 rounded-full shadow-md transition-opacity duration-300 ${canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          style={{ top: '50%', transform: 'translateY(-50%)' }}
        >
          <MdChevronRight size={16} className="text-gray-600" />
        </button>
      </div>
    </div>
  );
}

// CSS for hiding scrollbar
const style = document.createElement('style');
style.textContent = `
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;
document.head.appendChild(style);
